import { Box, Container, IconButton, Link, Stack, ThemeProvider, Typography } from "@mui/material";
import {
  FacebookRounded,
  Instagram,
  LinkedIn,
  LocationOnOutlined,
  PublicRounded
} from "@mui/icons-material";
import useAuth from "app/hooks/useAuth";
import { AppRoutes } from "app/utils/global_constants";
import nextHireLogo from "../../assets/logo/nexthire-transparent.webp";
import { Link as RouterLink } from "react-router-dom";
import landingTheme, { LANDING_LAYOUT } from "app/views/landing_page/theme";

const COMPANY_ADDRESS = "D-1, Saket District Center, Saket, New Delhi 110017";

const SOCIAL_LINKS = [
  {
    label: "Reyan Consultancy Services on Facebook",
    href: "https://www.facebook.com/home.php?ref=wizard",
    icon: FacebookRounded
  },
  {
    label: "Reyan Consultancy Services on LinkedIn",
    href: "https://www.linkedin.com/company/reyan-consultancy-services-rcs/",
    icon: LinkedIn
  },
  {
    label: "NextHire on Instagram",
    href: "https://www.instagram.com/rcs_nexthire/",
    icon: Instagram
  }
];

const linkSx = {
  color: "text.secondary",
  display: "inline-flex",
  fontSize: 13,
  lineHeight: 1.5,
  textAlign: "left",
  transition: "color 160ms ease",
  "&:hover": { color: "primary.main" }
};

function FooterLink({ item }) {
  if (item.route) {
    return (
      <Link
        component={RouterLink}
        to={item.route}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        sx={linkSx}
      >
        {item.label}
      </Link>
    );
  }

  return (
    <Link
      href={item.href}
      rel={item.external ? "noreferrer" : undefined}
      target={item.external ? "_blank" : undefined}
      sx={linkSx}
    >
      {item.label}
    </Link>
  );
}

function FooterColumn({ title, links }) {
  return (
    <Box>
      <Typography component="h3" sx={{ fontSize: 14, fontWeight: 800, mb: 2.5 }}>
        {title}
      </Typography>
      <Stack component="ul" spacing={1.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
        {links.map((item) => (
          <Box component="li" key={item.label}>
            <FooterLink item={item} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function DetailedFooterContent() {
  const { user } = useAuth();
  const postJobRoute =
    user?.role === "company" ? AppRoutes.ROUTE_POST_JOB : AppRoutes.ROUTE_LOGIN_COMPANY;

  const footerGroups = [
    {
      title: "For Employers",
      links: [
        { label: "Features", href: "/#features" },
        { label: "Post Jobs", route: postJobRoute },
        {
          label: "Implementation & Support",
          href: "https://www.reyan.co.in/software-impementation-support.php",
          external: true
        },
        {
          label: "Staff Augmentation",
          href: "https://www.reyan.co.in/staff-augmentation-services.php",
          external: true
        },
        {
          label: "HR Consultancy",
          href: "https://www.reyan.co.in/hr-consultancy.php",
          external: true
        }
      ]
    },
    {
      title: "For Candidates",
      links: [
        { label: "Search Jobs", route: "/jobs" },
        { label: "Quick Apply", route: "/jobs/job-apply" },
        { label: "Create an Account", route: AppRoutes.ROUTE_SIGNUP_CANDIDATE },
        { label: "Candidate Login", route: AppRoutes.ROUTE_LOGIN_CANDIDATE }
      ]
    },
    {
      title: "Resources",
      links: [
        { label: "Reyan Home", href: "https://www.reyan.co.in/", external: true },
        {
          label: "About Us",
          href: "https://www.reyan.co.in/about.php",
          external: true
        },
        {
          label: "Careers",
          href: "https://www.reyan.co.in/job-openings.php",
          external: true
        },
        { label: "Privacy Policy", route: "/privacyPolicy" }
      ]
    }
  ];

  return (
    <Box
      id="resources"
      component="footer"
      sx={{
        bgcolor: "landing.surfaceLow",
        borderTop: "1px solid",
        borderColor: "divider",
        pb: 4,
        pt: { xs: 6, sm: 7, lg: 10 },
        scrollMarginTop: 90
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: LANDING_LAYOUT.maxWidth, px: { xs: 2, sm: 3 } }}>
        <Box
          sx={{
            display: "grid",
            gap: { xs: 4.5, sm: 5, lg: 4 },
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "1.25fr repeat(4, minmax(0, 1fr))"
            },
            mb: { xs: 6, sm: 7, lg: 10 }
          }}
        >
          <Box sx={{ gridColumn: { sm: "1 / -1", lg: "auto" }, maxWidth: 280 }}>
            <Stack
              component={RouterLink}
              to="/"
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ color: "inherit", textDecoration: "none", width: "fit-content" }}
            >
              <Box
                component="img"
                src={nextHireLogo}
                alt="NextHire"
                sx={{
                  display: "block",
                  height: "auto",
                  width: 100
                }}
              />
            </Stack>

            <Typography color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.7, mt: 2.5 }}>
              The AI-powered recruitment platform for modern hiring teams and job seekers.
            </Typography>

            <Stack direction="row" spacing={1} mt={3}>
              {SOCIAL_LINKS.map(({ label, href, icon: SocialIcon }) => (
                <IconButton
                  key={label}
                  aria-label={label}
                  href={href}
                  rel="noreferrer"
                  target="_blank"
                  size="small"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    color: "text.secondary",
                    "&:hover": {
                      bgcolor: "primary.main",
                      borderColor: "primary.main",
                      color: "common.white"
                    }
                  }}
                >
                  <SocialIcon fontSize="small" />
                </IconButton>
              ))}
            </Stack>
          </Box>

          {footerGroups.map((group) => (
            <FooterColumn key={group.title} title={group.title} links={group.links} />
          ))}

          <Box>
            <Typography component="h3" sx={{ fontSize: 14, fontWeight: 800, mb: 2.5 }}>
              Contact Us
            </Typography>
            <Stack component="ul" spacing={2} sx={{ listStyle: "none", m: 0, p: 0 }}>
              <Stack component="li" direction="row" spacing={1.25} alignItems="flex-start">
                <PublicRounded color="primary" sx={{ fontSize: 19, mt: 0.15 }} />
                <Link
                  href="https://www.reyan.co.in/contact-us.php"
                  rel="noreferrer"
                  target="_blank"
                  sx={linkSx}
                >
                  Contact Reyan
                </Link>
              </Stack>
              <Stack component="li" direction="row" spacing={1.25} alignItems="flex-start">
                <LocationOnOutlined color="primary" sx={{ fontSize: 19, mt: 0.15 }} />
                <Typography color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.5 }}>
                  {COMPANY_ADDRESS}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Box>

        <Stack
          alignItems="center"
          borderTop="1px solid"
          borderColor="divider"
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={2}
          pt={4}
        >
          <Typography
            color="text.secondary"
            sx={{ fontSize: 12, textAlign: { xs: "center", sm: "left" } }}
          >
            Copyright © {new Date().getFullYear()} NextHire by Reyan Consultancy Services.
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="center"
            spacing={0}
            useFlexGap
            sx={{ columnGap: 3, rowGap: 1 }}
          >
            <Link
              href="https://www.reyan.co.in/about.php"
              rel="noreferrer"
              target="_blank"
              sx={{ ...linkSx, fontSize: 12 }}
            >
              About Us
            </Link>
            <Link
              href="https://www.reyan.co.in/contact-us.php"
              rel="noreferrer"
              target="_blank"
              sx={{ ...linkSx, fontSize: 12 }}
            >
              Contact Us
            </Link>
            <Link
              href="https://www.reyan.co.in/job-openings.php"
              rel="noreferrer"
              target="_blank"
              sx={{ ...linkSx, fontSize: 12 }}
            >
              Careers
            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

function LimitedFooterContent() {
  return (
    <Box
      id="resources"
      component="footer"
      sx={{
        bgcolor: "landing.surfaceLow",
        borderTop: "1px solid",
        borderColor: "divider",
        py: 2,
        scrollMarginTop: 90
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: LANDING_LAYOUT.maxWidth, px: { xs: 2, sm: 3 } }}>
        {/* <Stack
          component={RouterLink}
          to="/"
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ color: "inherit", textDecoration: "none", width: "fit-content" }}
        >
          <Box
            component="img"
            src={nextHireLogo}
            alt="NextHire"
            sx={{
              display: "block",
              height: "auto",
              width: 100
            }}
          />
        </Stack> */}
        <Stack
          alignItems="center"
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Typography
            color="text.secondary"
            sx={{ fontSize: 12, textAlign: { xs: "center", sm: "left" } }}
          >
            Copyright © {new Date().getFullYear()} NextHire by Reyan Consultancy Services.
          </Typography>
          <Stack
            direction="row"
            flexWrap="wrap"
            justifyContent="center"
            spacing={0}
            useFlexGap
            sx={{ columnGap: 3, rowGap: 1 }}
          >
            <Link
              href="https://www.reyan.co.in/about.php"
              rel="noreferrer"
              target="_blank"
              sx={{ ...linkSx, fontSize: 12 }}
            >
              About Us
            </Link>
            <Link
              href="https://www.reyan.co.in/contact-us.php"
              rel="noreferrer"
              target="_blank"
              sx={{ ...linkSx, fontSize: 12 }}
            >
              Contact Us
            </Link>
            <Link
              href="https://www.reyan.co.in/job-openings.php"
              rel="noreferrer"
              target="_blank"
              sx={{ ...linkSx, fontSize: 12 }}
            >
              Careers
            </Link>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

export default function Footer({ showDetailedFooter }) {
  return (
    <ThemeProvider theme={landingTheme}>
      {showDetailedFooter ? <DetailedFooterContent /> : <LimitedFooterContent />}
    </ThemeProvider>
  );
}
